import {
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  ActiveFilterChips,
  type MultiSelectFilterField,
} from "./components/ActiveFilterChips";
import { AnalysisFilters } from "./components/AnalysisFilters";
import { CompositionChart } from "./components/CompositionChart";
import { CompositionTable } from "./components/CompositionTable";
import { FiltersPanel } from "./components/FiltersPanel";
import type {
  LayerWeightLimits,
  ScrapMixFilterOption,
  ScrapMixFilters,
  ScrapMixStatistic,
} from "./models";
import { DEFAULT_LAYER_WEIGHT_LIMITS } from "./models";
import { useScrapMixAnalysis } from "./useScrapMixAnalysis";

const moment = getMomentTz();
const EMPTY_FILTER_OPTIONS: ScrapMixFilterOption[] = [];
const EMPTY_STATISTICS: ScrapMixStatistic[] = [];

const createDefaultFilters = (): ScrapMixFilters => ({
  from: moment().subtract(29, "days").startOf("day"),
  to: moment().startOf("day"),
  excludeOutOfRangeLayers: false,
  shifts: [],
  crews: [],
  gradeIds: [],
  recipeFamilyIds: [],
  operatorIds: [],
  chargeNumbers: [],
  heatNames: [],
});

const cloneFilters = (filters: ScrapMixFilters): ScrapMixFilters => ({
  ...filters,
  from: filters.from.clone(),
  to: filters.to.clone(),
  shifts: [...filters.shifts],
  crews: [...filters.crews],
  gradeIds: [...filters.gradeIds],
  recipeFamilyIds: [...filters.recipeFamilyIds],
  operatorIds: [...filters.operatorIds],
  chargeNumbers: [...filters.chargeNumbers],
  heatNames: [...filters.heatNames],
});

const parseNumericSetting = (value: unknown, fallback: number) => {
  if (typeof value !== "string" || value.trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const ScrapMixAnalysisPage = () => {
  const { state: utlSettings } = useUTLSettingsContext();
  const plantZoneReady = Boolean(utlSettings?.settings?.TimeZone);
  const minLayerWeightSetting =
    utlSettings?.settings?.SCRAP_MIX_ANALYSIS_MIN_LAYER_WEIGHT;
  const maxLayerWeightSetting =
    utlSettings?.settings?.SCRAP_MIX_ANALYSIS_MAX_LAYER_WEIGHT;
  const layerWeightLimits = useMemo<LayerWeightLimits>(
    () => ({
      min: parseNumericSetting(
        minLayerWeightSetting,
        DEFAULT_LAYER_WEIGHT_LIMITS.min,
      ),
      max: parseNumericSetting(
        maxLayerWeightSetting,
        DEFAULT_LAYER_WEIGHT_LIMITS.max,
      ),
    }),
    [minLayerWeightSetting, maxLayerWeightSetting],
  );
  const [appliedFilters, setAppliedFilters] =
    useState<ScrapMixFilters>(createDefaultFilters);
  const [draftFilters, setDraftFilters] = useState<ScrapMixFilters>(() =>
    cloneFilters(appliedFilters),
  );
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(true);
  const [expandedRecipes, setExpandedRecipes] = useState<Set<number>>(
    () => new Set(),
  );

  const query = useScrapMixAnalysis(
    appliedFilters,
    layerWeightLimits,
    plantZoneReady,
  );
  const analysis = query.data;
  const filterOptions = analysis?.filterOptions ?? EMPTY_FILTER_OPTIONS;
  const statistics = analysis?.statistics ?? EMPTY_STATISTICS;

  const handleApply = useCallback(() => {
    setErrorDismissed(false);
    setAppliedFilters(cloneFilters(draftFilters));
  }, [draftFilters]);

  const handleClear = useCallback(() => {
    const defaults = createDefaultFilters();
    setErrorDismissed(false);
    setDraftFilters(cloneFilters(defaults));
    setAppliedFilters(defaults);
  }, []);

  const handleRemoveDraftValue = useCallback(
    (field: MultiSelectFilterField, value: string) => {
      setDraftFilters((current) => ({
        ...current,
        [field]: current[field].filter((item) => item !== value),
      }));
    },
    [],
  );

  const handleToggleRecipe = useCallback((recipeFamilyId: number) => {
    setExpandedRecipes((current) => {
      const next = new Set(current);
      if (next.has(recipeFamilyId)) next.delete(recipeFamilyId);
      else next.add(recipeFamilyId);
      return next;
    });
  }, []);

  const errorMessage =
    !errorDismissed && query.error instanceof Error ? query.error.message : "";

  return (
    <>
      <HelmetDexteel title="Scrap Mix Analysis" />
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Scrap Mix Analysis
        </Typography>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          sx={{ minWidth: 0 }}
        >
          <FiltersPanel
            open={filtersPanelOpen}
            onToggle={() => setFiltersPanelOpen((current) => !current)}
          >
            <AnalysisFilters
              filters={draftFilters}
              options={filterOptions}
              layerWeightLimits={layerWeightLimits}
              isLoading={query.isFetching}
              onChange={setDraftFilters}
              onApply={handleApply}
              onClear={handleClear}
            />
          </FiltersPanel>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <ActiveFilterChips
              filters={draftFilters}
              options={filterOptions}
              onRemove={handleRemoveDraftValue}
            />

            {query.isFetching ? (
              <LinearProgress aria-label="Loading analysis" />
            ) : null}

            <Paper elevation={1} sx={{ p: 1.5, overflow: "hidden" }}>
              <CompositionChart
                statistics={statistics}
                expandedRecipes={expandedRecipes}
                onToggleRecipe={handleToggleRecipe}
              />
            </Paper>

            <Paper elevation={1} sx={{ p: 1.5, overflow: "hidden" }}>
              <CompositionTable
                statistics={statistics}
                isLoading={query.isLoading}
                expandedRecipes={expandedRecipes}
                onToggleRecipe={handleToggleRecipe}
              />
            </Paper>
          </Stack>
        </Stack>
      </Stack>

      <ErrorModal error={errorMessage} onHide={() => setErrorDismissed(true)} />
    </>
  );
};
